'use client';

import * as React from 'react';

import type { Settings } from '@/types/v2/settings';
import { applyDefaultSettings } from '@/lib/v2/settings/apply-default-settings';
import type { PrimaryColor } from '@/styles/theme-v2/theme/types';

export interface SettingsContextValue {
  settings: Settings;
  setSettings: (settings: Settings) => void;
}

export const SettingsContext = React.createContext<SettingsContextValue>({
  settings: applyDefaultSettings({}),
  setSettings: () => {
    // noop
  },
});

export interface SettingsProviderProps {
  children: React.ReactNode;
  settings: Settings;
}

export function SettingsProvider({ children, settings: initialSettings }: SettingsProviderProps): React.JSX.Element {
  const [state, setState] = React.useState<Settings>(initialSettings);

  React.useEffect(() => {
    // Use professional blue as primary color to match brand
    const updatedSettings = {
      ...initialSettings,
      primaryColor: 'professionalBlue' as PrimaryColor,
    };
    setState(updatedSettings);
  }, [initialSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: state,
        setSettings: (newSettings: Settings) => {
          // Use professional blue as primary color to match brand
          const updatedSettings = {
            ...newSettings,
            primaryColor: 'professionalBlue' as PrimaryColor,
          };
          setState(updatedSettings);
        },
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const SettingsConsumer = SettingsContext.Consumer;
